import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

/* ============================================================
   useGlbBackground — CRUD for GLB 3D background config
   Stores in site_settings table with key 'glb_background'.
   ============================================================ */

export interface GlbBackgroundConfig {
  enabled: boolean
  glb_url: string
  instance_count: number
  color_override: string
  opacity: number
  scale: number
  rotation_speed: number
  float_speed: number
  float_intensity: number
  movement_pattern: 'float' | 'orbit' | 'drift' | 'scatter'
}

export const DEFAULT_GLB_CONFIG: GlbBackgroundConfig = {
  enabled: false,
  glb_url: '',
  instance_count: 5,
  color_override: '#ff69b4',
  opacity: 0.6,
  scale: 1,
  rotation_speed: 1,
  float_speed: 1.5,
  float_intensity: 1,
  movement_pattern: 'float',
}

const SETTING_KEY = 'glb_background'

export function useGlbBackground(tenantId: string | undefined) {
  const queryClient = useQueryClient()
  const queryKey = ['glb-background', tenantId]

  const { data: config, isLoading: loading } = useQuery({
    queryKey,
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('tenant_id', tenantId!)
        .eq('key', SETTING_KEY)
        .maybeSingle()

      if (error) throw error
      if (!data) return DEFAULT_GLB_CONFIG
      return { ...DEFAULT_GLB_CONFIG, ...(data.value as Partial<GlbBackgroundConfig>) }
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (update: Partial<GlbBackgroundConfig>) => {
      if (!tenantId) throw new Error('No tenant')
      const merged = { ...(config ?? DEFAULT_GLB_CONFIG), ...update }

      const { error } = await supabase
        .from('site_settings')
        .upsert(
          {
            tenant_id: tenantId,
            key: SETTING_KEY,
            value: merged as unknown as import('@/types/database.types').Json,
          },
          { onConflict: 'tenant_id,key' }
        )

      if (error) throw error
      return merged
    },
    onSuccess: (merged) => {
      queryClient.setQueryData(queryKey, merged)
    },
  })

  return {
    config: config ?? DEFAULT_GLB_CONFIG,
    loading,
    saving: saveMutation.isPending,
    save: saveMutation.mutateAsync,
  }
}

/* Consumer-facing: lightweight read-only fetch */
export function useGlbBackgroundConfig(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['glb-background-consumer', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('tenant_id', tenantId!)
        .eq('key', SETTING_KEY)
        .maybeSingle()

      if (error) return null
      if (!data) return null
      const cfg = { ...DEFAULT_GLB_CONFIG, ...(data.value as Partial<GlbBackgroundConfig>) }
      return cfg.enabled && cfg.glb_url ? cfg : null
    },
  })
}
